/**
 * portalPath — dual-path helper for subdomain vs preview environments.
 *
 * Production (per-subdomain document root): admin.khmerhealthvn.com → "/login"
 * Lovable preview (single host, multi-portal): *.lovable.app → "/admin/login"
 *
 * Usage:
 *   const p = portalPath("admin");
 *   navigate(p("/login"));        // "/login" on subdomain, "/admin/login" on preview
 *   navigate(p("/dashboard"));
 *
 * Detection: if hostname starts with `<portal>.` (e.g. admin.khmerhealthvn.com)
 * or equals the portal subdomain, we are on the dedicated subdomain → no prefix.
 * Otherwise we are on Lovable preview / shared host → keep prefix.
 */
export type PortalId = "admin" | "agent" | "guest" | "hospital" | "vendor";

export function isOnPortalSubdomain(portal: PortalId, hostname?: string): boolean {
  if (typeof window === "undefined" && !hostname) return false;
  const host = (hostname ?? window.location.hostname).toLowerCase();
  // Subdomain match: admin.khmerhealthvn.com, admin.something.com, etc.
  // Exclude any *.lovable.app / *.lovable.dev (preview) which always use prefix.
  if (host.endsWith(".lovable.app") || host.endsWith(".lovable.dev")) return false;
  return host === portal || host.startsWith(`${portal}.`);
}

/**
 * Returns a function that prefixes paths for the current environment.
 * Pass paths starting with `/` (canonical, no portal prefix).
 */
export function portalPath(portal: PortalId) {
  const onSub = isOnPortalSubdomain(portal);
  return (path: string): string => {
    // Normalize
    const clean = path.startsWith("/") ? path : `/${path}`;
    if (onSub) {
      // Strip an accidentally-included portal prefix
      const stripped = clean.replace(new RegExp(`^/${portal}(?=/|$)`), "") || "/";
      return stripped;
    }
    // Preview: ensure prefix exactly once
    if (clean === "/") return `/${portal}`;
    if (clean.startsWith(`/${portal}/`) || clean === `/${portal}`) return clean;
    return `/${portal}${clean}`;
  };
}
